import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from '@common_components/inputs/toggleSwitch.component';
import { EventTableColumnKey, EVENTS_TABLE_COLUMNS_META } from '../../constants/extraction.constants';

interface ColumnsSelectorProps {
  selectedColumns: EventTableColumnKey[];
  onChangeSelectedColumns: ( columns: EventTableColumnKey[] ) => void;
}

const ColumnsSelector: React.FC<ColumnsSelectorProps> = ( { selectedColumns, onChangeSelectedColumns } ) => {
  const { t } = useTranslation();
  const [ isOpen, setIsOpen ] = useState<boolean>( false );
  const buttonRef = useRef<HTMLButtonElement | null>( null );
  const menuRef = useRef<HTMLDivElement | null>( null );
  const [ menuPosition, setMenuPosition ] = useState<{ top: number; left: number } | null>( null );

  const toggleColumn = ( key: EventTableColumnKey ) => {
    const isChecked = selectedColumns.includes( key );
    if ( isChecked ) {
      const remaining = selectedColumns.filter( columnKey => { return columnKey !== key; } );
      if ( remaining.length === 0 ) {
        return;
      }
      onChangeSelectedColumns( remaining );
    } else {
      onChangeSelectedColumns( [ ...selectedColumns, key ] );
    }
  };

  const handleToggleOpen = () => {
    setIsOpen( prev => {
      const next = !prev;
      if ( next && buttonRef.current ) {
        const rect = buttonRef.current.getBoundingClientRect();
        const width = 224; // w-56 => 14rem => 224px
        setMenuPosition( {
          top: rect.bottom + 8,
          left: rect.right - width,
        } );
      }
      return next;
    } );
  };

  useEffect( () => {
    if ( typeof document === 'undefined' ) { return; }

    const handleClickOutside = ( event: MouseEvent ) => {
      if ( !isOpen ) { return; }
      const target = event.target as Node | null;
      if ( !target ) { return; }

      if ( buttonRef.current?.contains( target ) ) { return; }
      if ( menuRef.current?.contains( target ) ) { return; }

      setIsOpen( false );
    };

    document.addEventListener( 'mousedown', handleClickOutside );
    return () => {
      document.removeEventListener( 'mousedown', handleClickOutside );
    };
  }, [ isOpen ] );

  return (
    <div className="relative">
      <button
        type="button"
        className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 cursor-pointer select-none"
        onClick={handleToggleOpen}
        ref={buttonRef}
      >
        <span aria-hidden="true" className="pointer-events-none">···</span>
      </button>
      {isOpen && typeof document !== 'undefined' && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="absolute z-50 w-80 max-h-[70vh] bg-white border border-gray-200 rounded-xl shadow-xl p-4 text-base overflow-y-auto touch-pan-y"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <div className="flex flex-col gap-3">
            {EVENTS_TABLE_COLUMNS_META.map( columnMeta => {
              const checked = selectedColumns.includes( columnMeta.key );
              return (
                <div
                  key={columnMeta.key}
                  className="py-2 cursor-pointer hover:bg-gray-50 rounded-lg px-2"
                  onClick={() => { toggleColumn( columnMeta.key ); }}
                >
                  <ToggleSwitch
                    label={t( columnMeta.labelKey ) as string}
                    isActive={checked}
                    onToggle={() => { toggleColumn( columnMeta.key ); }}
                    labelPosition="left"
                    textSize="text-gray-700 truncate text-base"
                  />
                </div>
              );
            } )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnsSelector;
